import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { EventType } from '@lib/models/enums';
import { InteractionPartUtils } from '@lib/models/InteractionPart';
import type { PartRendererProps } from './types';
import { useAiChatServices } from '../../AiChatContext';

/**
 * Renders an `askauth` tool call — the agent asking the user to SIGN IN mid-conversation. Two faces,
 * mirroring AskUserPart:
 *  - INTERACTIVE (unanswered) — the optional reason plus a "Sign in to continue" button. Clicking it
 *    invokes the host-supplied `onRequestAuth` (a popup keeps the chat WebSocket alive) and, on a
 *    signed token, sends it back over the WS (ChatAction.AnswerHumanTool, keyed by the tool-call id),
 *    unblocking the parked server-side tool so the turn continues as an authenticated user.
 *  - LOCKED (answered / reload) — a short status line (the tool's returned sentinel, or "✓ Signed in").
 *
 * The library stays IdP-agnostic: it never runs the login itself. If the host didn't wire
 * `onRequestAuth`, the card renders a disabled fallback.
 */

const asObj = (v: unknown): Record<string, unknown> | null =>
    v && typeof v === 'object' ? (v as Record<string, unknown>) : null;

/** Read the `reason` string the tool passed (args may be an object or a JSON string). */
function readReason(args: unknown): string | undefined {
    const a = asObj(args);
    if (a && typeof a.reason === 'string' && a.reason.trim()) return a.reason.trim();
    if (typeof args === 'string' && args.trim()) {
        try {
            const p = asObj(JSON.parse(args));
            if (p && typeof p.reason === 'string' && p.reason.trim()) return p.reason.trim();
        } catch {
            /* ignore */
        }
    }
    return undefined;
}

/** Collapse the tool's echoed response into a plain sentinel string, or null. */
function sentinelOf(raw: unknown): string | null {
    let v: unknown = raw;
    if (v && typeof v === 'object') {
        const collapsed = InteractionPartUtils.tryCollapseCharIndexedObject(v);
        if (typeof collapsed === 'string') v = collapsed;
    }
    if (typeof v !== 'string') return null;
    const t = v.trim().replace(/^\(+|\)+$/g, '').trim();
    return t.length ? t : null;
}

// Only `part` is required; the rest of PartRendererProps is optional so a pane-level sheet could
// render this standalone. Still assignable to the PartRenderer registry (which passes full props).
type AskAuthPartProps = Partial<PartRendererProps> & { part: PartRendererProps['part']; embedded?: boolean };

type Phase = 'idle' | 'pending' | 'done' | 'failed';

const AskAuthPart: React.FC<AskAuthPartProps> = ({ part, embedded = false }) => {
    const { agentGatewayService, onRequestAuth, renderAuthPrompt } = useAiChatServices();
    const [phase, setPhase] = useState<Phase>('idle');

    const callId = part.functionDetails?.id;
    const reason = useMemo(() => readReason(part.functionDetails?.args), [part.functionDetails?.args]);

    // Answered = handled this session, OR the function response already carries a result (reload).
    const serverAnswer = part.eventType === EventType.FunctionResponse
        ? (part.functionDetails?.response ?? null)
        : null;
    const locked = phase === 'done' || serverAnswer != null || !callId;

    const outerSx = embedded
        // In the docked sheet: breathing room below the button so it isn't glued to the composer edge.
        ? { maxWidth: '100%', pb: 2 }
        : { border: '1px solid', borderColor: 'divider', borderRadius: 2, px: 2, py: 1.5, my: 0.5, maxWidth: '100%' };

    // ── Locked: short status summary ──────────────────────────────────────────
    if (locked) {
        const sentinel = phase === 'done' ? null : sentinelOf(serverAnswer);
        return (
            <Box sx={outerSx}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {sentinel ?? '✓ Conectado'}
                </Typography>
            </Box>
        );
    }

    // Shared by both surfaces: a host that collected the token hands it back here; the library owns the
    // WS answer + the locked state. Used by `renderAuthPrompt` (onToken) and the button path.
    const deliverToken = (token: string) => {
        if (!callId) return;
        agentGatewayService.answerHumanTool(callId, token);
        setPhase('done');
    };
    const cancel = () => setPhase('idle');

    const handleSignIn = async () => {
        if (!callId || !onRequestAuth) return;
        setPhase('pending');
        try {
            const token = await onRequestAuth();
            if (token) deliverToken(token);
            else cancel(); // user backed out — allow another attempt
          
            if (token) {
                agentGatewayService.answerHumanTool(callId, token);
                setPhase('done');
            } else {
                // User cancelled — allow another attempt.
                setPhase('idle');
            }
        } catch {
            setPhase('failed');
        }
    };

    // Host-supplied prompt body (e.g. inline email/provider form) takes precedence; it calls
    // onToken/onCancel. When present, the host OWNS the whole prompt UI — it receives `reason` via ctx and
    // decides how (and whether) to show it — so we do NOT render the default reason line, which would
    // otherwise duplicate whatever the host renders. Falls back to the simple `onRequestAuth` button (with
    // the default reason line), then to a notice.
    if (renderAuthPrompt) {
        return (
            <Box sx={outerSx}>
                {renderAuthPrompt({ reason, onToken: deliverToken, onCancel: cancel })}
            </Box>
        );
    }

    return (
        <Box sx={outerSx}>
            <Typography variant="body2" sx={{ mb: 1.25 }}>
                {reason || 'Faça login para continuar.'}
            </Typography>

            {onRequestAuth ? (
                <Stack direction="row" spacing={1} alignItems="center">
                    {/* Mirrors the home "entrar" affordance (HomeNav): mono, pill, outlined, lowercase. */}
                    <Button
                        onClick={handleSignIn}
                        disabled={phase === 'pending'}
                        startIcon={phase === 'pending'
                            ? <CircularProgress size={14} color="inherit" />
                            : undefined}
                        sx={(theme) => ({
                            fontFamily: theme.typography.fontFamilyMono,
                            fontSize: 12,
                            textTransform: 'none',
                            px: 2,
                            py: 1,
                            borderRadius: 999,
                            color: 'text.primary',
                            border: '1px solid',
                            borderColor: 'border.default',
                            '&:hover': { bgcolor: 'background.paper' },
                        })}
                    >
                        {phase === 'pending' ? 'entrando…' : 'entrar'}
                    </Button>
                    {phase === 'failed' && (
                        <Typography variant="caption" sx={{ color: 'error.main' }}>
                            Não foi possível entrar — tente novamente.
                        </Typography>
                    )}
                </Stack>
            ) : (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Login indisponível aqui.
                </Typography>
            )}
        </Box>
    );
};

export default AskAuthPart;
